
import { toast } from '@/components/ui/use-toast';

// Helper function to fetch journalist metrics
export const fetchJournalistMetrics = async (journalistId: string) => {
  try {
    const { supabase } = await import('@/integrations/supabase/client');
    
    // Fetch article count
    const { count: articleCount, error: countError } = await supabase
      .from('articles')
      .select('*', { count: 'exact', head: true })
      .eq('author_id', journalistId)
      .eq('status', 'published');
    
    if (countError) {
      console.error('Error fetching article count:', countError);
    }
    
    console.log('Article count:', articleCount);
    
    // Calculate years active based on created_at date
    const { data: profileData } = await supabase
      .from('profiles')
      .select('created_at')
      .eq('id', journalistId)
      .single();
      
    const createdAt = new Date(profileData?.created_at || Date.now());
    const currentYear = new Date().getFullYear();
    const yearsActive = Math.max(1, currentYear - createdAt.getFullYear());
    
    // Get follower count
    const { count: followerCount, error: followerError } = await supabase
      .from('journalist_follows')
      .select('*', { count: 'exact', head: true })
      .eq('journalist_id', journalistId);
    
    if (followerError) {
      console.error('Error fetching follower count:', followerError);
    }
    
    console.log('Follower count:', followerCount);
    
    return { articleCount, followerCount, yearsActive };
  } catch (error) {
    console.error('Error in fetchJournalistMetrics:', error);
    return { articleCount: 0, followerCount: 0, yearsActive: 1 };
  }
};

// Helper function to safely parse JSON arrays
export const parseJsonArray = (data: any): string[] => {
  try {
    // Check if data is already an array
    if (Array.isArray(data)) {
      return data;
    } else if (typeof data === 'string') {
      // Try to parse it as JSON
      try {
        const parsed = JSON.parse(data);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        console.error('Error parsing JSON string:', e);
        return [];
      }
    } else if (typeof data === 'object') {
      // It's an object, but not an array
      return [];
    }
    return [];
  } catch (e) {
    console.error('Error processing data:', e);
    return [];
  }
};

// Helper function to handle errors consistently
export const handleApiError = (error: any, customMessage: string) => {
  console.error(`${customMessage}:`, error);
  toast({
    variant: 'destructive',
    title: 'Error',
    description: error instanceof Error ? error.message : customMessage
  });
};

// Helper function to check follow status
export const checkFollowStatus = async (currentUserId: string, journalistId: string): Promise<boolean> => {
  try {
    const { supabase } = await import('@/integrations/supabase/client');
    console.log(`Checking if user ${currentUserId} follows journalist ${journalistId}`);
    
    const { data: followData, error: followError } = await supabase
      .from('journalist_follows')
      .select('*')
      .eq('user_id', currentUserId)
      .eq('journalist_id', journalistId)
      .maybeSingle();
    
    if (followError) {
      console.error('Error checking follow status:', followError);
    }
      
    console.log('Follow data:', followData);
    return !!followData;
  } catch (error) {
    console.error('Error in checkFollowStatus:', error);
    return false;
  }
};
